import { HttpErrorResponse, type HttpEvent, type HttpHandlerFn, type HttpHeaders, type HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { type Observable, tap } from 'rxjs';

import { redactHeaders, redactLogBody } from '../../shared/utils/redact-log.util';
import { redactUrl } from '../../shared/utils/redact-sensitive.util';
import { LeapBotDiagnosticsService } from '../services/leapbot/leapbot-diagnostics.service';

function headersToRecord(headers: HttpHeaders): Record<string, string> {
  return Object.fromEntries(headers.keys().map((name) => [name, headers.getAll(name)?.join(', ') ?? '']));
}

/**
 * Passive HTTP interceptor that copies the app's requests into the LeapBot recording log while a
 * screen recording is in progress. It never alters a request or a response, does nothing when no
 * recording is active, skips LeapBot's own traffic, and redacts headers, URLs and bodies before
 * anything is stored. Only Angular `HttpClient` traffic is seen (not Firestore or sign-in).
 */
export function leapBotNetworkCaptureInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const diagnostics = inject(LeapBotDiagnosticsService);
  if (!diagnostics.isRecording() || diagnostics.isLeapBotUrl(req.url)) return next(req);

  const startedAt = performance.now();
  const base = {
    method: req.method,
    url: redactUrl(req.urlWithParams),
    requestHeaders: redactHeaders(headersToRecord(req.headers)),
    requestBody: redactLogBody(req.body),
  };

  return next(req).pipe(
    tap({
      next: (event) => {
        if (!(event instanceof HttpResponse)) return;
        diagnostics.recordRequest({
          ...base,
          status: event.status,
          durationMs: Math.round(performance.now() - startedAt),
          responseHeaders: redactHeaders(headersToRecord(event.headers)),
          responseBody: redactLogBody(event.body),
          error: null,
        });
      },
      error: (error: unknown) => {
        const failure = error instanceof HttpErrorResponse ? error : null;
        diagnostics.recordRequest({
          ...base,
          status: failure?.status ?? 0,
          durationMs: Math.round(performance.now() - startedAt),
          responseHeaders: failure ? redactHeaders(headersToRecord(failure.headers)) : {},
          responseBody: failure ? redactLogBody(failure.error) : null,
          error: failure?.message ?? 'Network error',
        });
      },
    }),
  );
}
